/**
 * Map Hubwayz / Mishor search results → Gimmonix-like hotel Result rows
 * for Agents Hotels tab (searchoffer).
 */

import { HUBWAYZ_HOTEL_ID_PREFIX } from "./hubwayzHotelsTabConfig";
import { verifiedHubwayzListPrice } from "../unifiedSearchMockup/hubwayz/hubwayzVerifiedQuote";

const HUBWAYZ_IMAGE_CDN = "https://cdn-images.innstant-servers.com";
const HUBWAYZ_IMAGE_SIZE = "800x600";

function quoteCode(row) {
  return String(row?.offerCode ?? row?.offer_code ?? "").trim();
}

function quoteHotelId(row) {
  return String(row?.hotelId ?? row?.hotel_id ?? "").trim();
}

function offerCode(offer) {
  return String(offer?.code ?? offer?.offerCode ?? offer?.offer_code ?? "").trim();
}

function moneyAmount(node) {
  if (!node || typeof node !== "object") return null;
  const n = Number(node.amount);
  return Number.isFinite(n) ? n : null;
}

function absolutizeHubwayzImageUrl(raw) {
  const path = String(raw || "").trim().replace(/^\/+/, "");
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${HUBWAYZ_IMAGE_CDN}/${HUBWAYZ_IMAGE_SIZE}/${path}`;
}

function resolveHotelImage(staticHotel) {
  if (!staticHotel || typeof staticHotel !== "object") return null;
  if (staticHotel.mainImage) return absolutizeHubwayzImageUrl(staticHotel.mainImage);
  const images = Array.isArray(staticHotel.images) ? staticHotel.images : [];
  const mainId =
    staticHotel.mainImageId != null ? String(staticHotel.mainImageId) : null;
  const main =
    (mainId && images.find((img) => String(img?.id) === mainId)) || images[0] || null;
  if (!main) return null;
  if (typeof main === "string") return absolutizeHubwayzImageUrl(main);
  return absolutizeHubwayzImageUrl(
    main.url || main.src || main.path || main.large || main.medium || main.small
  );
}

/**
 * @param {{ results?: any[], hotelsById?: Record<string, any> }} mishorData
 * @param {{ city?: string, country?: string, searchRequest?: object|null }} meta
 * @returns {object[]}
 */
export function mapHubwayzToHotelSearchResults(mishorData, meta = {}) {
  const results = Array.isArray(mishorData?.results) ? mishorData.results : [];
  const hotelsById = mishorData?.hotelsById || {};
  /** @type {Map<string, any>} */
  const byHotel = new Map();

  for (const offer of results) {
    const items = Array.isArray(offer?.items) ? offer.items : [];
    const first = items[0] || {};
    const hotelId = first.hotelId != null ? String(first.hotelId) : null;
    if (!hotelId) continue;

    const net = moneyAmount(offer.netPrice);
    const currency = String(offer?.netPrice?.currency || "")
      .trim()
      .toUpperCase();
    const prev = byHotel.get(hotelId);
    const sortNet = Number.isFinite(net) ? net : Number.POSITIVE_INFINITY;

    if (prev) {
      prev._hubwayzOffers.push(offer);
      prev.roomOptionsCount = (prev.roomOptionsCount || 1) + 1;
      if (sortNet < prev._sortNet) {
        prev._sortNet = sortNet;
        prev.LowestPackagePrice = Number.isFinite(net) ? String(net) : prev.LowestPackagePrice;
        prev.supplierCurrency = currency || null;
        prev.supplierAmount = Number.isFinite(net) ? net : prev.supplierAmount;
        prev.hubwayzOfferCode = offer.code;
        prev._hubwayzCheapestOffer = offer;
      }
      continue;
    }

    const staticHotel = hotelsById[hotelId] || {};
    const name =
      staticHotel.name ||
      staticHotel.Name ||
      staticHotel.hotelName ||
      `Hotel ${hotelId}`;
    const stars =
      Number(staticHotel.stars || staticHotel.Stars || staticHotel.rating || 0) || 0;
    const city =
      staticHotel.city ||
      staticHotel.City ||
      meta.city ||
      "";
    const country =
      staticHotel.country || staticHotel.Country || meta.country || "";
    const address =
      staticHotel.address ||
      staticHotel.Address ||
      [city, country].filter(Boolean).join(", ");
    const imageUrl = resolveHotelImage(staticHotel);

    byHotel.set(hotelId, {
      ID: `${HUBWAYZ_HOTEL_ID_PREFIX}${hotelId}`,
      Id: `${HUBWAYZ_HOTEL_ID_PREFIX}${hotelId}`,
      hubwayzHotelId: hotelId,
      DisplayName: name,
      HotelName: name,
      Name: name,
      StarRating: String(stars || ""),
      Rating: stars,
      Address: address,
      City: city,
      country,
      LowestPackagePrice: Number.isFinite(net) ? String(net) : null,
      HighestPackagePrice: Number.isFinite(net) ? String(net) : null,
      supplierCurrency: currency || null,
      supplierAmount: Number.isFinite(net) ? net : null,
      displayCurrency: meta.displayCurrency || null,
      paymentCurrency: "ILS",
      DefaultImage: imageUrl
        ? { FullSize: imageUrl, Thumb: imageUrl }
        : null,
      Supplier: "Hubwayz",
      supplier: "hubwayz",
      _supplier: "hubwayz",
      provider: "hubwayz",
      Source: "hubwayz",
      hubwayzOfferCode: offer.code,
      hubwayzSearchRequest: meta.searchRequest || null,
      roomOptionsCount: 1,
      _hubwayzOffers: [offer],
      _hubwayzCheapestOffer: offer,
      _hubwayzStatic: staticHotel,
      _sortNet: sortNet,
      isCampaignHotel: false,
    });
  }

  return Array.from(byHotel.values())
    .sort((a, b) => a._sortNet - b._sortNet)
    .map(({ _sortNet, ...rest }) => {
      const codes = new Set(
        (rest._hubwayzOffers || []).map(offerCode).filter(Boolean)
      );
      const hotelId = String(rest.hubwayzHotelId || "").trim();
      const verifiedQuote = verifiedHubwayzListPrice(
        (meta.verifiedQuotes || []).filter((row) => {
          const code = quoteCode(row);
          if (code && codes.has(code)) return true;
          const rowHotel = quoteHotelId(row);
          return Boolean(hotelId && rowHotel && rowHotel === hotelId);
        })
      );
      return {
        ...rest,
        verifiedQuote,
        hubwayzSearchId: meta.searchId || verifiedQuote?.searchId || null,
      };
    });
}
